import { set } from 'jsonpointer';
import { safeParse } from '@hapi/bourne';
import { isRelativeDate, maxDate, minDate, parseUserTime } from './date';
import { identifierRegex } from './model/report';

const PARSED_FORM_DATA = Symbol('PARSED_FORM_DATA');

export interface ParsedFormFile {
  name: string;
  size: number;
  type: string;
  file: File;
}

export function parseFormData<T extends object>(data: FormData): T {
  if ((data as any)[PARSED_FORM_DATA]) {
    return (data as any)[PARSED_FORM_DATA];
  }
  const obj = Object.create(null) as T;
  const accruedMasks = Object.create(null) as any;
  for (const [key, value] of data) {
    let [pointer, type, ...args] = key.split(':');
    if (value instanceof File) {
      const { name, size, type } = value;
      const parsedFile = { name, size, type };
      Object.defineProperty(parsedFile, 'file', { enumerable: false, value });
      set(obj, '/' + pointer, parsedFile);
      continue;
    }
    if (typeof value !== 'string') {
      throw new Error(`Unexpected value type: ${value}`);
    }
    let parsedValue: string | boolean | number | null = value
      // https://www.w3.org/MarkUp/html-spec/html-spec_8.html#SEC8.2.1
      // The form field names and values are escaped: space characters are replaced by '+', and then
      // reserved characters are escaped as per [URL]; that is, non-alphanumeric characters are
      // replaced by `%HH', a percent sign and two hexadecimal digits representing the ASCII code of
      // the character. Line breaks, as in multi-line text field values, are represented as CR LF
      // pairs, i.e. `%0D%0A'.
      .replaceAll('\r\n', '\n');
    type ||= pointer.endsWith('_id') ? 'nullstring' : 'string';
    if (args.includes('parametric') && identifierRegex.test(value)) {
      parsedValue = value;
      set(obj, '/' + pointer, parsedValue);
      continue;
    }
    switch (type) {
      case 'string':
        parsedValue = value;
        break;
      case 'nullstring':
        parsedValue = value === '' ? null : value;
        break;
      case 'boolean':
        parsedValue = value === 'true';
        break;
      case 'number':
        parsedValue = value === '' ? null : parseFloat(value);
        break;
      case 'integer':
        parsedValue = value === '' ? null : parseInt(value, 10);
        break;
      case 'mask-shift': {
        const current = accruedMasks[pointer] ?? 0;
        parsedValue = current + 2 ** parseInt(value, 10);
        accruedMasks[pointer] = parsedValue;
        break;
      }
      case 'mask-sum': {
        const current = accruedMasks[pointer] ?? 0;
        parsedValue = current + parseInt(value, 10);
        accruedMasks[pointer] = parsedValue;
        break;
      }
      case 'date': {
        if (value === '') {
          parsedValue = null;
          break;
        }
        if (args.includes('relative') && isRelativeDate(value)) {
          parsedValue = value;
          break;
        }
        const d = new Date(value);
        if (
          isFinite(d.getTime()) &&
          d.getTime() >= minDate &&
          d.getTime() <= maxDate
        ) {
          parsedValue = d.toISOString().substr(0, 10);
          break;
        }
        parsedValue = null;
        break;
      }
      case 'month-date': {
        if (value === '') {
          parsedValue = null;
          break;
        }
        const d = new Date(`${value}-01`);
        if (
          isFinite(d.getTime()) &&
          d.getTime() >= minDate &&
          d.getTime() <= maxDate
        ) {
          parsedValue = d.toISOString().substr(0, 10);
          break;
        }
        parsedValue = null;
        break;
      }
      case 'time': {
        parsedValue = parseUserTime(value);
        break;
      }
      case 'json':
        parsedValue = safeParse(value);
        break;
      default:
        throw new Error(`Unexpected type directive "${type}" in "${key}".`);
    }
    set(obj, '/' + pointer, parsedValue);
  }
  (data as any)[PARSED_FORM_DATA] = obj;
  return obj;
}
